import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
import { AccountStationInterFace } from '../../../interface/account-stations-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableAccountStationFilterApplied,
  fetchAccountStation,
  setSortData,
} from '../../../redux/slices/AccountStationSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import PageTitle from '../../../shared-components/page-title/PageTitle';
import StationSidePanel from '../../../shared-components/table-side-panel/StationSidePanel';

interface StationsProperties {
  onClose: () => void;
}

/**
 * Stations table component
 */
function Stations({ onClose }: StationsProperties) {
  const { t } = useTranslation('account');
  const [searchParameters] = useSearchParams();
  const accountCustomId = searchParameters.get('accountCustomId');
  const { accountStation, limit, page, loading, totalPages, totalResults, sortBy, sortField } =
    useAppSelector((state) => state.accountStation);
  const dispatch = useAppDispatch();
  const previousAccountCustomId = useRef<string | null>(null);
  const devicesReference = useRef<HTMLDivElement | null>(null);
  const [selectedStation, setSelectedStation] = useState<AccountStationInterFace | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const column = [
    {
      label: t('station'),
      accessorKey: 'name' as keyof AccountStationInterFace,
      sortAllow: true,
      /**
       *  Side panel component to show on clicking the cell value
       */
      customData: (cachestation: AccountStationInterFace) => (
        <button
          type="button"
          className="accounts-management"
          onClick={() => {
            setSelectedStation(cachestation);
            setIsPanelOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setSelectedStation(cachestation);
              setIsPanelOpen(true);
            }
          }}>
          {cachestation.name}
        </button>
      ),
    },
    {
      label: t('location'),
      accessorKey: 'address' as keyof AccountStationInterFace,
      sortAllow: true,
    },
    {
      label: t('noOfDevice'),
      accessorKey: 'totalDevices' as keyof AccountStationInterFace,
      sortAllow: true,
      /**
       *  Side panel component to show on clicking the cell value
       */
      customData: (station: AccountStationInterFace) => (
        <span>{station?.totalDevices ?? 0}</span>
      ),
    },
  ];

  /**
   * Fetch Users list function
   */
  const fetchData = (data: any) => {
    if (!loading && accountCustomId) {
      dispatch(fetchAccountStation({ accountCustomId, ...data }));
    }
  };
  /**
   * Table pagination function
   * @param value
   */
  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: any) => {
    if (value.sortField && value.sortBy) {
      dispatch(setSortData({ sortField: value.sortField, sortBy: value.sortBy as 'asc' | 'desc' }));
    }
    fetchData({ sortBy, sortField, ...value });
  };

  /**
   * Handle sort change function
   * @param value
   */
  const handleSortChange = (value: any) => {
    if (value.sortField && value.sortBy) {
      dispatch(setSortData({ sortField: value.sortField, sortBy: value.sortBy as 'asc' | 'desc' }));
    }
    fetchData({ page: 1, limit, ...value });
  };

  useEffect(() => {
    if (
      accountStation.length === 0 ||
      (accountCustomId && previousAccountCustomId.current !== accountCustomId)
    ) {
      fetchData({
        page,
        limit,
        hardRefresh: true,
      });
      dispatch(disableAccountStationFilterApplied());
      previousAccountCustomId.current = accountCustomId;
    }
  }, [accountCustomId]);
  useEffect(() => {
    /**
     * Handle click outside the component to trigger onClose
     */
    const handleClickOutside = (event: MouseEvent) => {
      if (devicesReference.current && !devicesReference.current.contains(event.target as Node)) {
        if (isPanelOpen) {
          setIsPanelOpen(false);
          setSelectedStation(null);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div ref={devicesReference}>
      <PageTitle
        title={t('stations')}
        buttonSvgIcon={<CloseIcon />}
        buttonType="none"
        onClickButton={() => {
          if (isPanelOpen) {
            setIsPanelOpen(false);
            setSelectedStation(null);
          } else {
            onClose();
          }
        }}
        buttonLabel=" "
      />

      {isPanelOpen && selectedStation ? (
        <StationSidePanel height={72} fetchIdFrom="props" id={selectedStation._id} />
      ) : (
        <CustomTable
          header={column}
          sortData={{ sortBy, sortField }}
          serialNo
          limit={limit}
          totalPages={totalPages}
          data={accountStation}
          page={page}
          handleSortChange={handleSortChange}
          loading={loading}
          totalResults={totalResults}
          pageLimitChange={pageNateLimitChange}
          height={370}
        />
      )}
    </div>
  );
}

export default Stations;
